import { Button, Col, Label } from "reactstrap";
import { Heading, HeadingTag } from "components/common/Heading";
import {
  AboutYourLoan,
  accountNumberValidation,
  finalRepaymentDateValidation,
  ONE,
  remainingBalanceValidation,
  sortCodeValidation,
  THREE,
  validateAboutYourLoan,
  ZERO,
} from "domain/aboutYourLoan";
import { ValidInputField } from "components/common/InputField";
import { ValidNumberInput } from "components/common/NumberInputField";
import React, { useCallback, useMemo, useState } from "react";
import { isValid } from "validation/validation";
import {
  TREATMENT_TAKEN_RADIO_QUESTION,
  TREATMENT_OPTION_QUESTION,
  TREATMENT_OPTIONS_CHECKBOX_1,
  TREATMENT_OPTIONS_CHECKBOX_2,
  TREATMENT_OPTIONS_CHECKBOX_3,
  ALL_TREATEMENTS_TAKEN_WARNING_TEXT1,
  ALL_TREATEMENTS_TAKEN_WARNING_TEXT2_1,
  ALL_TREATEMENTS_TAKEN_WARNING_TEXT2_2,
  ABOUT_YOUR_LOAN_NOTE,
  ACCOUNT_NUMBER_QUESTION,
  ACCOUNT_NUMBER_TOOLTIP_MSG,
  LOAN_BALANCE_QUESTION,
  SORT_CODE_QUESTION,
  REPAYMENT_DATE_QUESTION,
  REPAYMENT_DATE_TOOLTIP_MSG,
  MAX_OPTION_TAKEN_LINK,
} from "./YourLoan.const";
import { NEED_HELP_URLS } from "components/common/Page/Navigation";
import { brand } from "services/envService";
import { CustomLabelRadioOptions } from "./CustomLabelRadioQuestions";
import { CheckBox } from "components/common/CheckBox";
import { VisibleSection } from "components/common/VisibleSection";
import { NeedHelp } from "components/common/NeedHelp";
import { ValidDatePicker } from "components/common/Datepicker";
import { DateRange } from "util/date";
import { CRHQuestions } from "./CRHQuestions";
import { START_YEAR, END_YEAR } from "domain/selectedTreatments";
import { CIRHQuestions } from "./CIRHQuestions";
import {
  Notification,
  Status,
} from "components/common/Notification/Notification";

export type AboutYourLoanFormProps = {
  aboutYourLoan: AboutYourLoan;
  onSubmit: (aboutYourLoan: AboutYourLoan) => void;
};

const BRAND = brand();

export const finalRepaymentDateDateRange = (): DateRange => {
  const startDate = new Date(START_YEAR);
  const endDate = new Date(END_YEAR);
  return [startDate, endDate];
};

export const AboutYourLoanForm = (props: AboutYourLoanFormProps) => {
  const [aboutYourLoan, setAboutYourLoan] = React.useState<AboutYourLoan>(
    props.aboutYourLoan
  );

  const {
    treatmentTaken,
    CRHTaken,
    CIRHTaken,
    fullTermExtensionTaken,
    CRHWithTermExtensionCountTaken,
    CIRHWithTermExtensionCountTaken,
  } = aboutYourLoan;

  const [CRHYes, setCRHYes] = useState<boolean>(
    treatmentTaken ? !!CRHTaken : false
  );
  const [CIRHYes, setCIRHYes] = useState<boolean>(
    treatmentTaken ? !!CIRHTaken : false
  );
  const validateForm = useCallback(
    () => validateAboutYourLoan(aboutYourLoan),
    [aboutYourLoan]
  );
  const isFormValid = isValid(validateForm);
  const valid = useMemo(
    () => isFormValid(aboutYourLoan),
    [aboutYourLoan, isFormValid]
  );

  const isAllTreatmentsTaken =
    CRHTaken === THREE && CIRHTaken === ONE && fullTermExtensionTaken;
  const whichTreatmentOptionTakenRule =
    ((CRHTaken && CRHTaken <= THREE) ||
      (CIRHTaken && CIRHTaken <= ONE) ||
      fullTermExtensionTaken) &&
    !isAllTreatmentsTaken;
  const enabledNextButton =
    valid &&
    (treatmentTaken === null
      ? false
      : treatmentTaken
        ? whichTreatmentOptionTakenRule
        : true);

  const handleChangeRaw = (date: React.FocusEvent<HTMLInputElement>) => {
    setAboutYourLoan({
      ...aboutYourLoan,
      finalRepaymentDate: null,
    });
    const userInputDate = new Date(date.currentTarget.value);
    const [minDate, maxDate] = finalRepaymentDateDateRange() || [];
    if (userInputDate) {
      if (isNaN(userInputDate.getTime())) {
        return;
      }
      if (
        minDate &&
        minDate.getTime() <= userInputDate.getTime() &&
        maxDate &&
        userInputDate.getTime() <= maxDate.getTime()
      ) {
        setAboutYourLoan({
          ...aboutYourLoan,
          finalRepaymentDate: new Date(date.currentTarget.value),
        });
      }
    }
  };

  return (
    <div className="narrow-container">
      <Col className="content-wrapper sticky-container">
        <div>
          <Heading
            tag={HeadingTag.h1}
            size={1}
            className="zb-heading1 screen-header"
            title="About your loan"
          />
          <ValidInputField
            value={aboutYourLoan.accountNumber}
            onChange={(accountNumber) =>
              setAboutYourLoan({ ...aboutYourLoan, accountNumber })
            }
            id="accountNumber"
            name="accountNumber"
            validate={accountNumberValidation}
            label={ACCOUNT_NUMBER_QUESTION}
          />
          <ValidInputField
            value={aboutYourLoan.sortCode}
            onChange={(sortCode) =>
              setAboutYourLoan({ ...aboutYourLoan, sortCode })
            }
            id="sortCode"
            name="sortCode"
            validate={sortCodeValidation}
            label={SORT_CODE_QUESTION}
          />
          <div className="needHelp-container">
            <NeedHelp redirectUrl={NEED_HELP_URLS[BRAND]} />
          </div>
          <ValidNumberInput
            value={aboutYourLoan.remainingBalance ?? ZERO}
            onChange={(remainingBalance) =>
              setAboutYourLoan({ ...aboutYourLoan, remainingBalance })
            }
            id="remainingBalance"
            name="remainingBalance"
            validate={remainingBalanceValidation}
            label={LOAN_BALANCE_QUESTION}
            tooltipText={ACCOUNT_NUMBER_TOOLTIP_MSG}
          />
          <ValidDatePicker
            id="finalRepaymentDate"
            name="finalRepaymentDate"
            onChange={(finalRepaymentDate) =>
              setAboutYourLoan({ ...aboutYourLoan, finalRepaymentDate })
            }
            onChangeRaw={handleChangeRaw}
            selectedDate={aboutYourLoan.finalRepaymentDate}
            label={REPAYMENT_DATE_QUESTION}
            tooltipText={REPAYMENT_DATE_TOOLTIP_MSG}
            range={finalRepaymentDateDateRange()}
            validate={finalRepaymentDateValidation}
          />
          <br />
          <div className="treatment-options-radios">
            <CustomLabelRadioOptions
              name="treatmentOptions"
              title={TREATMENT_TAKEN_RADIO_QUESTION}
              value={treatmentTaken}
              onChange={(treatmentTaken) => {
                setAboutYourLoan({
                  ...aboutYourLoan,
                  treatmentTaken,
                  CRHTaken: treatmentTaken ? CRHTaken : null,
                  CIRHTaken: treatmentTaken ? CIRHTaken : null,
                  CRHWithTermExtensionCountTaken: treatmentTaken
                    ? CRHWithTermExtensionCountTaken
                    : null,
                  CIRHWithTermExtensionCountTaken: treatmentTaken
                    ? CIRHWithTermExtensionCountTaken
                    : null,
                  fullTermExtensionTaken: treatmentTaken
                    ? fullTermExtensionTaken
                    : false,
                });
                setCRHYes(false);
                setCIRHYes(false);
              }}
            />
          </div>
          <VisibleSection visible={!!treatmentTaken}>
            <div className="checkboxes-container">
              <Label>{TREATMENT_OPTION_QUESTION}</Label>
              <CheckBox
                id="capitalRepaymentHolidayCheck"
                label={TREATMENT_OPTIONS_CHECKBOX_1}
                checked={CRHYes}
                onChange={(CRHYes) => {
                  setCRHYes(CRHYes);
                  setAboutYourLoan({
                    ...aboutYourLoan,
                    CRHTaken: CRHYes
                      ? CRHTaken
                      : fullTermExtensionTaken || CIRHYes
                        ? ZERO
                        : null,
                    CIRHTaken: CIRHYes ? CIRHTaken : ZERO,
                    CRHWithTermExtensionCountTaken: CRHYes
                      ? null
                      : fullTermExtensionTaken || CIRHYes
                        ? ZERO
                        : null,
                    CIRHWithTermExtensionCountTaken: CIRHYes
                      ? CIRHWithTermExtensionCountTaken
                      : ZERO,
                  });
                }}
              />
              <VisibleSection visible={CRHYes}>
                <CRHQuestions
                  onCRHTakenCountChange={(CRHTaken: number) =>
                    setAboutYourLoan({
                      ...aboutYourLoan,
                      CRHTaken: CRHYes
                        ? CRHTaken
                        : treatmentTaken
                          ? null
                          : ZERO,
                      CIRHTaken: CIRHYes ? CIRHTaken : ZERO,
                      CRHWithTermExtensionCountTaken: null,
                      CIRHWithTermExtensionCountTaken: CIRHYes
                        ? CIRHWithTermExtensionCountTaken
                        : ZERO,
                    })
                  }
                  CRHTakenvalue={CRHTaken}
                  onCRHWithExtensionCountChange={(
                    CRHWithTermExtensionCountTaken: number
                  ) =>
                    setAboutYourLoan({
                      ...aboutYourLoan,
                      CRHWithTermExtensionCountTaken:
                        CRHTaken && CRHTaken > ZERO
                          ? CRHWithTermExtensionCountTaken
                          : CRHTaken && CRHTaken === ZERO
                            ? null
                            : ZERO,
                    })
                  }
                  CRHWithTermExtensionTakenValue={
                    CRHWithTermExtensionCountTaken
                  }
                />
              </VisibleSection>
              <CheckBox
                id="capitalInterestRepaymentHolidayCheck"
                label={TREATMENT_OPTIONS_CHECKBOX_2}
                checked={CIRHYes}
                onChange={(CIRHYes) => {
                  setCIRHYes(CIRHYes);
                  setAboutYourLoan({
                    ...aboutYourLoan,
                    CIRHTaken: CIRHYes
                      ? ONE
                      : fullTermExtensionTaken || CRHYes
                        ? ZERO
                        : null,
                    CRHTaken: CRHYes ? CRHTaken : ZERO,
                    CIRHWithTermExtensionCountTaken: CIRHYes
                      ? null
                      : fullTermExtensionTaken || CRHYes
                        ? ZERO
                        : null,
                    CRHWithTermExtensionCountTaken: CRHYes
                      ? CRHWithTermExtensionCountTaken
                      : ZERO,
                  });
                }}
              />
              <VisibleSection visible={CIRHYes}>
                <CIRHQuestions
                  CIRHTaken={CIRHWithTermExtensionCountTaken}
                  onChange={(CIRHWithTermExtensionCountTaken) => {
                    setAboutYourLoan({
                      ...aboutYourLoan,
                      CIRHTaken: CIRHYes ? CIRHTaken : null,
                      CIRHWithTermExtensionCountTaken:
                        CIRHWithTermExtensionCountTaken,
                      CRHTaken: CRHYes ? CRHTaken : ZERO,
                      CRHWithTermExtensionCountTaken:
                        !!CRHWithTermExtensionCountTaken
                          ? CRHWithTermExtensionCountTaken
                          : ZERO,
                    });
                  }}
                />
              </VisibleSection>
              <CheckBox
                id="extendLoanTermCheck"
                label={TREATMENT_OPTIONS_CHECKBOX_3}
                checked={aboutYourLoan.fullTermExtensionTaken}
                onChange={(fullTermExtensionTaken) =>
                  setAboutYourLoan({
                    ...aboutYourLoan,
                    fullTermExtensionTaken: fullTermExtensionTaken,
                    CRHTaken: CRHYes ? CRHTaken : ZERO,
                    CIRHTaken: CIRHYes ? CIRHTaken : ZERO,
                    CRHWithTermExtensionCountTaken: CRHYes
                      ? CRHWithTermExtensionCountTaken
                      : ZERO,
                    CIRHWithTermExtensionCountTaken: CIRHYes
                      ? CIRHWithTermExtensionCountTaken
                      : ZERO,
                  })
                }
              />
            </div>
            <VisibleSection visible={isAllTreatmentsTaken}>
              <div className="checkboxes-note newline">
                <Notification title={""} status={Status.INFO}>
                  <b>Note:</b> {ALL_TREATEMENTS_TAKEN_WARNING_TEXT1}
                  <p>
                    {ALL_TREATEMENTS_TAKEN_WARNING_TEXT2_1}&nbsp;
                    <a
                      rel="noreferrer"
                      target="_blank"
                      href={MAX_OPTION_TAKEN_LINK[BRAND]}
                    >
                      here
                    </a>
                    &nbsp;{ALL_TREATEMENTS_TAKEN_WARNING_TEXT2_2}
                  </p>
                </Notification>
              </div>
            </VisibleSection>
          </VisibleSection>
          <div className="newline main-note">
            <Notification title={""} status={Status.INFO}>
              <b>Note:</b> {ABOUT_YOUR_LOAN_NOTE}
            </Notification>
          </div>
        </div>
        <div className="button-end">
          <Button
            color="primary"
            size="lg"
            disabled={!enabledNextButton}
            onClick={() => props.onSubmit(aboutYourLoan)}
          >
            Next
          </Button>
        </div>
      </Col>
    </div>
  );
};
